import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Platform } from 'react-native';
import { useLocalParticipant, useVoiceAssistant } from '@livekit/components-react';

const BED_DUCKED_VOLUME = 0.125;
const BED_NORMAL_VOLUME = 0.5;
const BED_FADE_STEPS = 20;
export const PREFLIGHT_LEAVE_OVERLAY_MS = 1000;

function writeBedVolume(el: HTMLAudioElement, value: number): void {
  // HTMLMediaElement.volume is the playback API, not React state.
  // eslint-disable-next-line no-param-reassign
  el.volume = Math.max(0, Math.min(1, value));
}

type UseWaitingLoungeAtmosphereOptions = {
  /** True while the student is on the cohort waiting step inside pre_flight (bed loop only). */
  active: boolean;
  /** Mute LiveKit mic uplink on orientation and waiting so film audio does not leak. */
  muteUplink: boolean;
  waitingBedUrl: string | null | undefined;
  /** When true, tear down bed audio (leave lobby, ID reject, or 1:1). */
  unload: boolean;
  /** Show the pre-briefing fade overlay (~1s). */
  showLeavingOverlay: boolean;
};

/**
 * Web-only waiting bed loop with mute (bed only), VO ducking, mic uplink mute,
 * and a ~1s fade-in to half volume.
 */
export function useWaitingLoungeAtmosphere({
  active,
  muteUplink,
  waitingBedUrl,
  unload,
  showLeavingOverlay,
}: UseWaitingLoungeAtmosphereOptions) {
  const { localParticipant } = useLocalParticipant();
  const { state: voiceAssistantState } = useVoiceAssistant();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [bedMuted, setBedMuted] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const bedMutedRef = useRef(bedMuted);
  bedMutedRef.current = bedMuted;
  const voiceAssistantStateRef = useRef(voiceAssistantState);
  voiceAssistantStateRef.current = voiceAssistantState;

  const isWeb = Platform.OS === 'web';

  const resolveBedVolume = useCallback(() => {
    if (bedMutedRef.current) return 0;
    return voiceAssistantStateRef.current === 'speaking'
      ? BED_DUCKED_VOLUME
      : BED_NORMAL_VOLUME;
  }, []);

  const clearBedFade = useCallback(() => {
    if (fadeTimerRef.current) {
      clearInterval(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  const fadeBedVolume = useCallback((
    audio: HTMLAudioElement,
    from: number,
    to: number,
    durationMs: number,
    onDone?: () => void,
  ) => {
    clearBedFade();
    if (durationMs <= 0 || from === to) {
      writeBedVolume(audio, to);
      onDone?.();
      return;
    }
    const stepMs = durationMs / BED_FADE_STEPS;
    let step = 0;
    fadeTimerRef.current = setInterval(() => {
      step += 1;
      const next = from + (to - from) * (step / BED_FADE_STEPS);
      writeBedVolume(audio, next);
      if (step >= BED_FADE_STEPS) {
        writeBedVolume(audio, to);
        clearBedFade();
        onDone?.();
      }
    }, stepMs);
  }, [clearBedFade]);

  // The film and waiting bed must not leak into the room. Do not turn the
  // microphone back on when that ends: pre-flight and the briefing stay silent
  // until a supervisor allows speech, and the exam view turns it on itself.
  useEffect(() => {
    if (!muteUplink) return undefined;
    localParticipant.setMicrophoneEnabled(false).catch(() => undefined);
    return undefined;
  }, [muteUplink, localParticipant]);

  useEffect(() => {
    if (!isWeb || !active || unload || !waitingBedUrl) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      clearBedFade();
      return undefined;
    }

    const audio = new Audio(waitingBedUrl);
    audio.loop = true;
    audio.volume = 0;
    audioRef.current = audio;
    audio.play().catch(() => undefined);
    fadeBedVolume(audio, 0, resolveBedVolume(), PREFLIGHT_LEAVE_OVERLAY_MS);

    return () => {
      clearBedFade();
      audio.pause();
      audio.src = '';
      if (audioRef.current === audio) {
        audioRef.current = null;
      }
    };
    // Mute only changes volume — do not recreate the looping element.
    // resolveBedVolume reads mute/duck from refs so a recreate still starts quiet.
  }, [active, clearBedFade, fadeBedVolume, isWeb, resolveBedVolume, unload, waitingBedUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const target = resolveBedVolume();
    if (fadeTimerRef.current) {
      // Fade-in still running: mute snaps to silence; duck/unmute keep the ramp.
      if (target === 0) {
        clearBedFade();
        audio.volume = 0;
      }
      return;
    }
    audio.volume = target;
  }, [bedMuted, clearBedFade, resolveBedVolume, voiceAssistantState]);

  useEffect(() => {
    if (!showLeavingOverlay) {
      setOverlayVisible(false);
      return undefined;
    }
    setOverlayVisible(true);
    const audio = audioRef.current;
    if (audio && isWeb) {
      fadeBedVolume(audio, audio.volume, 0, PREFLIGHT_LEAVE_OVERLAY_MS, () => {
        audio.pause();
      });
    }
    const hideTimer = setTimeout(() => {
      setOverlayVisible(false);
    }, PREFLIGHT_LEAVE_OVERLAY_MS);
    return () => {
      clearTimeout(hideTimer);
      clearBedFade();
    };
  }, [clearBedFade, fadeBedVolume, isWeb, showLeavingOverlay]);

  const toggleBedMute = useCallback(() => {
    setBedMuted((prev) => {
      const next = !prev;
      bedMutedRef.current = next;
      const audio = audioRef.current;
      if (audio) {
        clearBedFade();
        audio.volume = resolveBedVolume();
      }
      return next;
    });
  }, [clearBedFade, resolveBedVolume]);

  return {
    bedMuted,
    toggleBedMute,
    showBedMute: Boolean(isWeb && active && !unload && waitingBedUrl),
    overlayVisible,
  };
}
